import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Paper,
    List,
    Skeleton,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';

import EventFilters from './EventFilters';
import EventListItem from './EventListItem';
import { dateUtils } from '../../../../utils';
import ProgressLoader from '../../../../components/ProgressLoader';
import { useDeleteEventMutation, useGetEventsQuery } from '../../../../api/eventsApiSlice';
import { selectCurrentUser } from '../../../authentication/authSlice';
import { roles } from '../../../../config/roles';

const yearEventsCache = new Map();
const YEAR_FETCH_PAGE_SIZE = 5000;

const getEventTimestamp = (eventStart) => {
    const [datePart, timePart = '00:00'] = eventStart.split(' ');
    const [day, month, year] = datePart.split('.');

    return new Date(`${year}-${month}-${day}T${timePart}:00`).getTime();
};

const EventList = () => {
    const [deleteEvent] = useDeleteEventMutation();
    const currentUser = useSelector(selectCurrentUser);

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 960px
    const isMd = useMediaQuery(theme.breakpoints.up('md')); // > 960px

    const [selectedYear, setSelectedYear] = useState(dateUtils.getCurrentYear());
    const [selectedMonth, setSelectedMonth] = useState(dateUtils.getCurrentMonthShort());
    const [yearEvents, setYearEvents] = useState([]);
    const [yearHydrated, setYearHydrated] = useState(false);

    const years = dateUtils.getFilterYears();
    const months = dateUtils.getAllMonthNamesShort();
    const selectedMonthNumber = useMemo(() => {
        const monthIndex = months.indexOf(selectedMonth);
        return monthIndex >= 0
            ? String(monthIndex + 1).padStart(2, '0')
            : undefined;
    }, [months, selectedMonth]);

    const queryArgs = useMemo(() => ({
        pageSize: YEAR_FETCH_PAGE_SIZE,
        page: 1,
        year: selectedYear,
        summary: true
    }), [selectedYear]);

    const cachedEvents = yearEventsCache.get(selectedYear);

    const { currentData, isLoading, isFetching } = useGetEventsQuery(queryArgs, {
        skip: !selectedYear || Boolean(cachedEvents)
    });

    useEffect(() => {
        if (cachedEvents) {
            setYearEvents(cachedEvents);
            setYearHydrated(true);
            return;
        }

        setYearEvents([]);
        setYearHydrated(false);
    }, [cachedEvents, selectedYear]);

    useEffect(() => {
        if (!currentData) {
            return;
        }

        const mappedEvents = (currentData.tapahtumat || []).map((event) => ({
            ...event,
            date: dateUtils.getDateFields(event.aloitus),
            sortTime: getEventTimestamp(event.aloitus)
        })).sort((firstEvent, secondEvent) => firstEvent.sortTime - secondEvent.sortTime);

        yearEventsCache.set(selectedYear, mappedEvents);
        setYearEvents(mappedEvents);
        setYearHydrated(true);
    }, [currentData, selectedYear]);

    const handleYearChange = useCallback((event, newValue) => setSelectedYear(newValue), []);
    const handleMonthChange = useCallback((event, newValue) => setSelectedMonth(newValue), []);

    const deleteEventById = useCallback(async (event) => {
        try {
            await deleteEvent(event).unwrap();
            setYearEvents((previousEvents) => {
                const updatedEvents = previousEvents.filter((currentEvent) => currentEvent.id !== event.id);
                yearEventsCache.set(selectedYear, updatedEvents);
                return updatedEvents;
            });
        } catch (error) {
            console.log(error);
        }
    }, [deleteEvent, selectedYear]);

    const eventsToDisplay = useMemo(() => {
        const canSeeOwnDrafts = [roles.admin, roles.suunnittelija, roles.muokkaaja].includes(currentUser?.role);

        return yearEvents
            .filter((event) => event.status !== 'luonnos' || (canSeeOwnDrafts && event.user === currentUser?.userId))
            .filter((event) => event.date.year === selectedYear)
            .filter((event) => event.date.month === selectedMonthNumber)
            .sort((firstEvent, secondEvent) => firstEvent.sortTime - secondEvent.sortTime);
    }, [currentUser?.role, currentUser?.userId, selectedMonthNumber, selectedYear, yearEvents]);

    const showInitialLoader = !yearHydrated && (isLoading || isFetching || yearEvents.length === 0);

    const getListHeight = () => {
        if (isXs) return '58vh';
        if (isSm) return '68vh';
        return '72vh';
    };

    const loadingSkeleton = Array.from({ length: isXs ? 3 : 5 }, (_, index) => (
        <Box key={index} sx={{ px: { xs: 1, sm: 2 }, py: 1.5 }}>
            <Skeleton variant="rounded" height={isXs ? 88 : 96} animation="wave" sx={{ borderRadius: 4 }} />
        </Box>
    ));

    let content;

    if (showInitialLoader) {
        content = <ProgressLoader msg="Ladataan tapahtumia..." />;
    } else {
        if (eventsToDisplay?.length) {
            content = eventsToDisplay.map((event) => (
                <EventListItem
                    deleteEvent={deleteEventById}
                    key={event.id}
                    event={event}
                />
            ));
        } else {
            content = (
                <Alert
                    severity="info"
                    sx={{
                        justifyContent: 'center',
                        borderRadius: 3,
                        m: 2,
                    }}
                >
                    Tälle kuulle ei vielä ole lisätty tapahtumia
                </Alert>
            );
        }
    }

    return (
        <Paper
            elevation={2}
            sx={{
                minHeight: getListHeight(),
                maxWidth: 'lg',
                mx: 'auto',
                my: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 2, md: 4 },
                borderRadius: { xs: 2, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                contain: 'layout paint',
            }}
        >
            <EventFilters
                values={years}
                selected={selectedYear}
                handleChange={handleYearChange}
            />
            <EventFilters
                values={months}
                selected={selectedMonth}
                handleChange={handleMonthChange}
                itemsPerPage={isMd ? 12 : 4}
            />
            <List
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    outline: 0,
                    mt: { xs: 1, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    pb: { xs: 1, sm: 2 },
                    contain: 'layout paint',
                    '& .MuiListItem-root': {
                        mb: { xs: 1, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                }}
                aria-busy={showInitialLoader}
            >
                {showInitialLoader ? loadingSkeleton : content}
            </List>
        </Paper>
    );
};

export default EventList;